import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Plus, Search, User, Clock, Filter } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';

interface PreferenceCard {
  preference_card_id: number;
  provider_id: number;
  procedure_name: string;
  procedure_cpt_code: string;
  procedure_category: string;
  equipment_list: any[];
  supplies_list: any[];
  medications_list: any[];
  patient_position: string;
  preferred_anesthesia_type: string;
  estimated_duration_minutes: number;
  times_used: number;
  last_used_date: string | null;
  is_active: boolean;
  created_at: string;
}

export function PreferenceCardsRoute() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterProvider, setFilterProvider] = useState<string>('all');

  // Fetch all preference cards
  const { data: cards, isLoading } = useQuery({
    queryKey: ['preference-cards', searchTerm, filterCategory, filterProvider],
    queryFn: async () => {
      let query = supabase
        .from('surgeon_preference_cards')
        .select('*')
        .eq('is_active', true)
        .order('times_used', { ascending: false });

      if (searchTerm) {
        query = query.or(`procedure_name.ilike.%${searchTerm}%,procedure_category.ilike.%${searchTerm}%`);
      }

      if (filterCategory !== 'all') {
        query = query.eq('procedure_category', filterCategory);
      }

      if (filterProvider !== 'all') {
        query = query.eq('provider_id', parseInt(filterProvider));
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as PreferenceCard[];
    },
  });

  // Get unique categories and providers for filters
  const categories = cards
    ? [...new Set(cards.map((c) => c.procedure_category))].sort()
    : [];
  
  const providers = cards
    ? [...new Set(cards.map((c) => c.provider_id))].sort()
    : [];

  return (
    <div className="min-h-full bg-pt-background">
      {/* Header */}
      <header className="border-b border-pt-border/60">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-semibold">
              Surgeon Preference Cards
            </h1>
            <p className="text-sm text-pt-text-muted mt-1">
              Pre-configured surgical setup templates
            </p>
          </div>
          <button
            onClick={() => navigate('/preference-cards/new')}
            className="flex items-center gap-2 bg-pt-gold text-black px-4 py-2 rounded-lg hover:bg-pt-gold/90 transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            New Card
          </button>
        </div>
      </header>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="bg-pt-panel border border-pt-border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pt-text-muted" />
              <input
                type="text"
                placeholder="Search procedures..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-pt-dark border border-pt-input-border rounded-lg text-pt-text placeholder:text-pt-text-dim focus:outline-none focus:border-pt-gold"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pt-text-muted pointer-events-none" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-pt-dark border border-pt-input-border rounded-lg text-pt-text focus:outline-none focus:border-pt-gold appearance-none cursor-pointer"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Provider Filter */}
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pt-text-muted pointer-events-none" />
              <select
                value={filterProvider}
                onChange={(e) => setFilterProvider(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-pt-dark border border-pt-input-border rounded-lg text-pt-text focus:outline-none focus:border-pt-gold appearance-none cursor-pointer"
              >
                <option value="all">All Surgeons</option>
                {providers.map((providerId) => (
                  <option key={providerId} value={providerId}>
                    Provider #{providerId}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-10">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="pt-spinner mx-auto mb-4" />
            <p className="text-pt-text-muted">Loading preference cards...</p>
          </div>
        ) : cards && cards.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((card) => (
              <PreferenceCardTile key={card.preference_card_id} card={card} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="No preference cards found"
            hint="Cards capture a surgeon's equipment, supplies, and setup for a procedure so the room can be ready before the patient rolls back."
            action={
              <button
                onClick={() => navigate('/preference-cards/new')}
                className="text-pt-gold hover:underline"
              >
                Create your first card
              </button>
            }
          />
        )}
      </div>
    </div>
  );
}

interface PreferenceCardTileProps {
  card: PreferenceCard;
}

function PreferenceCardTile({ card }: PreferenceCardTileProps) {
  const navigate = useNavigate();

  const totalItems =
    (card.equipment_list?.length || 0) +
    (card.supplies_list?.length || 0) +
    (card.medications_list?.length || 0);

  return (
    <button
      onClick={() => navigate(`/preference-cards/${card.preference_card_id}`)}
      className="bg-pt-panel border border-pt-border rounded-lg p-5 text-left hover:border-pt-gold transition-colors group"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-1 group-hover:text-pt-gold transition-colors">
            {card.procedure_name}
          </h3>
          <p className="text-xs text-pt-text-muted uppercase tracking-wide">
            {card.procedure_category}
          </p>
        </div>
        <span className="text-xs bg-pt-dark border border-pt-input-border px-2 py-1 rounded">
          {card.procedure_cpt_code}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-pt-border">
        <div>
          <div className="text-2xl font-bold text-pt-gold">{totalItems}</div>
          <div className="text-xs text-pt-text-muted">Items</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-pt-cyan">{card.times_used}</div>
          <div className="text-xs text-pt-text-muted">Uses</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-pt-text">{card.estimated_duration_minutes}</div>
          <div className="text-xs text-pt-text-muted">Minutes</div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <User className="w-4 h-4 text-pt-text-muted" />
          <span className="text-pt-text-muted">Provider #{card.provider_id}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-pt-text-muted" />
          <span className="text-pt-text-muted">
            {card.last_used_date
              ? `Last used ${new Date(card.last_used_date).toLocaleDateString()}`
              : 'Never used'}
          </span>
        </div>
      </div>

      {/* Item Breakdown */}
      <div className="mt-4 pt-4 border-t border-pt-border">
        <div className="flex flex-wrap gap-2 text-xs">
          {card.equipment_list && card.equipment_list.length > 0 && (
            <span className="bg-pt-dark border border-pt-input-border px-2 py-1 rounded">
              {card.equipment_list.length} Equipment
            </span>
          )}
          {card.supplies_list && card.supplies_list.length > 0 && (
            <span className="bg-pt-dark border border-pt-input-border px-2 py-1 rounded">
              {card.supplies_list.length} Supplies
            </span>
          )}
          {card.medications_list && card.medications_list.length > 0 && (
            <span className="bg-pt-dark border border-pt-input-border px-2 py-1 rounded">
              {card.medications_list.length} Medications
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
